import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../routes';
import { useMixPanel } from '../../components/MixPanel/useMixPanel';
import { useGoogleAnalytics } from '../../components/GoogleAnalytics/useGoogleAnalytics';
import { checkPaymentStatus, getAuthCodes } from '../../store/auth/auth.thunks';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ErrorIcon, SpinnerIcon } from './icons';
import {
  Background,
  Button,
  ButtonSize,
  ButtonVariant,
} from '../../components';
import { ACCOUNT_URL, OUTLOOK_ADDIN } from '../../constants/config';
import { ClientType, PaymentStatus } from '../../store/auth/auth.types';
import { withPageTransition } from '../../hoc';
import { MixpanelEvent } from '../../components/MixPanel/events/events';
import { useWindowSize } from '../../hooks';
import SsuLogoutFooter from '../../components/SsuLogoutFooter/SsuLogoutFooter';

const Processing: React.FC = () => {
  const user = useAppSelector((state) => state.auth.user);
  const paymentStatus = useAppSelector((state) => state.auth.paymentStatus);
  const dashboardAuthCode = useAppSelector(
    (state) => state.auth.dashboardAuthCode
  );

  const isPaymentStatusError = useAppSelector(
    (state) => state.auth.isError.paymentStatus
  );

  const dispatch = useAppDispatch();

  const history = useHistory();

  const { trackEvent, isMixPanelInitialized } = useMixPanel();
  const { trackGAEvent, isGoogleAnalyticsInitialized } = useGoogleAnalytics();

  const { width } = useWindowSize();
  const isDesktopView = width > 1024;

  useEffect(() => {
    if (
      isMixPanelInitialized &&
      paymentStatus !== PaymentStatus.Complete &&
      !dashboardAuthCode
    ) {
      trackEvent(MixpanelEvent.ARRIVED_TO_PROCESSING_PAGE);
    }
  }, [isMixPanelInitialized, paymentStatus, dashboardAuthCode, trackEvent]);

  useEffect(() => {
    if (isGoogleAnalyticsInitialized) {
      trackGAEvent('pageview', 'signup.processing-page');
    }
  }, [isGoogleAnalyticsInitialized, trackGAEvent]);

  useEffect(() => {
    if (user) {
      dispatch(checkPaymentStatus());
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (isPaymentStatusError) {
      trackEvent(MixpanelEvent.PROCESSING_PAGE_PAYMENT_SUBMIT_FAILED);
    }
  }, [isPaymentStatusError, trackEvent]);

  useEffect(() => {
    if (paymentStatus === PaymentStatus.Complete && isDesktopView) {
      trackEvent(MixpanelEvent.PROCESSING_PAGE_PAYMENT_SUBMITTED);
      dispatch(getAuthCodes({ client: ClientType.DASHBOARD }));
    }
  }, [dispatch, paymentStatus, trackEvent, isDesktopView]);

  useEffect(() => {
    if (dashboardAuthCode && window.name !== OUTLOOK_ADDIN.FLOW_NAME) {
      window.location.href = `${ACCOUNT_URL}?authCode=${dashboardAuthCode}`;
    }
  }, [dashboardAuthCode]);

  const attrs = {
    container: {
      className: 'processing',
    },
    content: {
      className: `processing__content${
        isPaymentStatusError ? ' processing__content--error' : ''
      }`,
    },
    title: {
      className: 'processing__title',
    },
    text: {
      className: 'processing__text',
    },
    icon: {
      className: 'processing__icon',
    },
    iconAnimated: {
      className: 'processing__icon processing__icon--animated',
    },
    footer: {
      className: 'processing__footer',
    },
    paymentPageButton: {
      size: ButtonSize.XS,
      variant: ButtonVariant.Dark,
      onClick: () => {
        trackEvent(MixpanelEvent.PROCESSING_PAGE_RETRY_PAYMENT);
        history.push(ROUTES.PRICING);
      },
    },
    goToDashboardButton: {
      className: 'processing__button',
      size: ButtonSize.XS,
      variant: ButtonVariant.Secondary,
      onClick: () => {
        trackEvent(MixpanelEvent.PROCESSING_PAGE_PAYMENT_SUBMITTED);
        dispatch(getAuthCodes({ client: ClientType.DASHBOARD }));
      },
    },
    contactSupportButton: {
      className: 'processing__contact-support',
      href: 'mailto:support@substrata.me',
      target: '_blank',
      rel: 'noopener noreferrer',
    },
  };

  const renderContent = () => {
    if (isPaymentStatusError) {
      return (
        <>
          <ErrorIcon {...attrs.icon} />
          <span {...attrs.title}>
            {paymentStatus === PaymentStatus.StripeError
              ? 'Unable to Confirm Payment'
              : 'Payment Failed'}
          </span>
          <span {...attrs.text}>
            {paymentStatus === PaymentStatus.StripeError ? (
              <span>
                We’re waiting to receive an update from our payment processor
                about your transaction. Confirmation may take a little longer
                than usual.
                <br />
                <br />
                If you have received a payment confirmation, your subscription
                should be activated soon.
                <br />
                <br />
                To continue, you can either retry the payment or contact support
                for help.
              </span>
            ) : (
              <span>
                We couldn’t process your payment for the Professional plan.
                Please verify your payment details. If the issue continues,
                contact our support for help.
              </span>
            )}
          </span>
          <div {...attrs.footer}>
            <a {...attrs.contactSupportButton}>Contact Support</a>
            <Button {...attrs.paymentPageButton}>
              <span>
                {paymentStatus === PaymentStatus.StripeError
                  ? 'Retry Payment'
                  : 'Try Again'}
              </span>
            </Button>
          </div>
        </>
      );
    }

    if (paymentStatus === PaymentStatus.Complete && !isDesktopView) {
      return (
        <>
          <span {...attrs.title}>
            Final step: Please continue in the desktop version of Google Chrome.
          </span>
          <span {...attrs.text}>
            Extension installation is only available on desktop.
          </span>
          <div {...attrs.footer}>
            <Button {...attrs.goToDashboardButton}>
              <span>Go to Dashboard</span>
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <SpinnerIcon {...attrs.iconAnimated} />
        <span {...attrs.title}>Processing...</span>
      </>
    );
  };

  return (
    <div {...attrs.container}>
      <div {...attrs.content}>
        {renderContent()}
        {isDesktopView ? <Background /> : null}
      </div>
      <SsuLogoutFooter />
    </div>
  );
};

export default withPageTransition(Processing);
